import React, { useRef } from 'react'
import Link from 'next/link'
import { useForm } from 'react-hook-form'
import { useRouter } from 'next/router'
import { uuid } from 'uuidv4'
import { useEffect, useState } from 'react'
import axios from 'axios'
import Alert from '../../Shared/Alert/Alert'
import Button_loading from '../../Shared/Loader/Button_loading'
import {
  auth_cookie_name,
  cookies_domain_name,
  default_site_url,
  global_auth_cookie_name,
  refresh_cookie_name,
  uuid_cookie_name,
} from '../../../constant/url'
import { deleteCookie, setCookie } from 'cookies-next'
import { encryptDataWithAES } from '../../../constant/functions'
import ReCAPTCHA from 'react-google-recaptcha'
import { MixpanelEventName, MixpanelPropertiesName, MixpanelTracking } from 'constant/mixpnael'
import mixpanel from 'mixpanel-browser'
import { discourse_redirect } from '@/utils/discourse_auth'
import { tradly_integration_redirect } from '@/utils/tradly_integration_auth'
import CustomLoading from '@/Shared/CustomLoading'
import { ai_icon } from 'constant/AllIcons'
import { build_examples } from 'data/WhatYouWantTobuild'

const Sign_up = ({
  sso,
  sig,
  is_log_in_by_discourse,
  is_log_in_by_tradly_integration,
  isLoggedIn,
}) => {
  const router = useRouter()
  const redirect_url = router.query.to || default_site_url
  const recaptchaRef = useRef(null)

  // Alert
  const [open_alert, setOpenAlert] = useState(false)
  const [message, setMessage] = useState('')
  const [alert_type, setAlert_type] = useState('success')
  const [isValidUser, setIsValidUser] = useState(false)
  const [captchaToken, setCaptchaToken] = useState(null)

  //
  const [isLoading, setIsLoading] = useState(false)
  const [countries, setCountries] = useState(null)

  // react hook form
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm()

  //
  useEffect(() => {
    setValue('uuid', uuid())
    setValue('type', 'tenant')

    // plan select and set and value
    if (localStorage.getItem('selected_plan')) {
      setValue('metadata.plan', localStorage.getItem('selected_plan'))
    } else {
      setValue('metadata.plan', 'community_monthly')
    }

    // channel set
    if (is_log_in_by_discourse) {
      setValue('metadata.channel', 'discourse')
    } else {
      setValue('metadata.channel', 'direct')
    }

    // country api calling
    axios
      .get('/api/countries')
      .then((res) => {
        setCountries(res.data.countries_res?.countries)
        if (res.data.IP_COUNTRY) {
          setValue(
            'metadata.country_id',
            res.data.countries_res?.countries.find((cn) => cn.code2 == res.data.IP_COUNTRY).id
          )
        } else {
          setValue('metadata.country_id', res.data.countries_res?.countries[0].id)
        }
      })
      .catch((err) => {
        const message = err?.response?.data?.message
        setIsLoading(false)

        if (message) {
          setOpenAlert(true)
          setAlert_type('error')
          setMessage(message)
        } else {
          if (message) {
            setOpenAlert(true)
            setAlert_type('error')
            setMessage('Something is wrong , please try latter')
          }
        }
      })
  }, [router])

  // Submit login form
  const onSubmit = async (data) => {
    // pass check
    if (data?.password?.length < 8) {
      setOpenAlert(true)
      setAlert_type('error')
      setMessage('Minimum password size is 8 letters.')
      return false
    }
    // console.log(data);
    if (captchaToken == null) {
      setOpenAlert(true)
      setAlert_type('error')
      setMessage('Please complete captcha verification to register.')
      return false
    } else {
      setIsLoading(true)
      axios
        .post('/api/register', {
          user_data: { ...data, custom_metadata: { expectations: data?.metadata?.expectations } },
          captcha_token: captchaToken,
        })
        .then(async (res) => {
          await window?.Reflio?.signup(data.email)
          // tenants api calling
          axios
            .get('/api/user_tenants_details', {
              params: { auth_key: res.data.user.key.auth_key },
            })
            .then(async (res2) => {
              setIsLoading(false)
              deleteCookie(auth_cookie_name)
              deleteCookie(refresh_cookie_name)
              deleteCookie(uuid_cookie_name)
              deleteCookie(global_auth_cookie_name)

              const default_tenant = res2.data.data.tenants?.find((tenant) => tenant?.default)

              //
              // cookie setting
              if (!res.data.user.email_verified) {
                setCookie(
                  global_auth_cookie_name,
                  encryptDataWithAES(JSON.stringify(res.data.user)),
                  {
                    maxAge: 86400,
                    domain: cookies_domain_name,
                    path: '/',
                  }
                )
              } else {
                setCookie(auth_cookie_name, encryptDataWithAES(res.data.user.key.auth_key), {
                  maxAge: 86400,
                  domain: cookies_domain_name,
                  path: '/',
                })
                setCookie(uuid_cookie_name, encryptDataWithAES(uuid()), {
                  maxAge: 860400,
                  domain: cookies_domain_name,
                  path: '/',
                })
                setCookie(refresh_cookie_name, encryptDataWithAES(res.data.user.key.refresh_key), {
                  maxAge: 860400,
                  domain: cookies_domain_name,
                  path: '/',
                })
                setCookie('auth_expiry_time', Date.now() + 86400000, {
                  domain: cookies_domain_name,
                  path: '/',
                  maxAge: 86400, // 1 day
                })
              }

              recaptchaRef?.current?.reset()
              setCaptchaToken(null)

              //mixpanel tracking
              mixpanel.identify(res.data.user.email)
              MixpanelTracking(MixpanelEventName.sign_up, {
                [MixpanelPropertiesName.user_name]: data?.name,
                [MixpanelPropertiesName.user_email]: data?.email,
                [MixpanelPropertiesName.tenant_name]: data?.app_name,
              })

              //freshwork api calling
              if (data?.metadata?.expectations) {
                const freshwork = await axios.post('/api/freshwork_contact', {
                  data: {
                    email: data?.email,
                    contact_info: {
                      custom_field: { cf_idea: data?.metadata?.expectations },
                    },
                  },
                })
              }

              if (is_log_in_by_discourse) {
                discourse_redirect({ sso, sig, router, user: res.data.user })
              } else if (is_log_in_by_tradly_integration) {
                tradly_integration_redirect({ sso, sig, router, user: res.data.user })
              } else {
                if (!res.data.user.email_verified) {
                  if (default_tenant?.super_user) {
                    router.push(`/verify_email?to=/onboarding`)
                  } else {
                    router.push(`/verify_email`)
                  }
                } else {
                  if (default_tenant?.super_user) {
                    router.push(`/onboarding`)
                  } else {
                    router.push(default_site_url)
                  }
                }
              }
            })
        })
        .catch((err) => {
          const message = err?.response?.data?.message
          setIsLoading(false)
          if (message) {
            setOpenAlert(true)
            setAlert_type('error')
            setMessage(message)
            recaptchaRef.current.reset()
            setCaptchaToken(null)
          } else {
            setOpenAlert(true)
            setAlert_type('error')
            setMessage('Something is wrong , please try latter')
            recaptchaRef.current.reset()
            setCaptchaToken(null)
          }
        })
    }
  }

  async function onChange(value) {
    setCaptchaToken(value)
  }

  // errors
  useEffect(() => {
    if (errors?.metadata?.app_name) {
      setOpenAlert(true)
      setAlert_type('error')
      setMessage(
        'App name should be only Alphabets. No special symbols or numbers allowed. App name character should not be more than 30.'
      )
    }
  }, [errors?.metadata?.app_name])

  return (
    <>
      {/* Alert */}
      <Alert
        className={` w-full md:w-[400px] lg:w-[450px]   fixed bottom-0 md:bottom-5   right-0 z-[99999999999999999999999999999999999999999] `}
        type={alert_type}
        open={open_alert}
        setOpen={setOpenAlert}
      >
        <p>{message}</p>
      </Alert>
      {isLoggedIn && sso && sig && <CustomLoading />}
      <div className="relative grid grid-cols-1  md:grid-cols-2 min-h-screen bg-[#F0F7F5] p-4">
        <div className=" w-full px-5 pt-10  flex items-start justify-center order-1 md:order-none  ">
          <div className="  flex flex-col gap-4 max-w-md">
            <h1 className="h1 !font-medium">Example prompts</h1>
            {build_examples?.map((item, index) => {
              if (index < 4) {
                return (
                  <div
                    key={item?.type}
                    className=" space-y-1 border border-gray-300 p-4 rounded-md "
                  >
                    <p className=" text-base font-bold">{item?.type}</p>
                    <p className=" text-base">{item?.description}</p>
                  </div>
                )
              }
            })}
          </div>
        </div>
        <div className="  pt-10 pb-16  bg-white shadow-onboarding_card p-8 rounded-3xl  w-full">
          {/* Page header */}
          <div className="max-w-3xl mx-auto text-center pb-12 md:pb-20">
            <h1 className="h1">Sign Up to Get Started</h1>
            <div className="text-gray-600 text-center mt-6 text-lg">
              Create a free account or{' '}
              <Link
                href={{
                  pathname: '/signin',
                  query: router.query ?? { to: 'https://superadmin.tradly.app' },
                }}
              >
                <a className="text-blue-600 hover:underline transition duration-150 ease-in-out">
                  Sign in
                </a>
              </Link>
            </div>
          </div>

          {/* Form */}
          <div className="max-w-sm mx-auto">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="flex flex-wrap -mx-3 mb-4">
                <div className="w-full px-3">
                  <label className="block text-gray-800 text-sm font-medium mb-1" htmlFor="name">
                    Your Name
                    <span className="text-red-600">*</span>
                  </label>
                  <input
                    id="name"
                    type="text"
                    className="form-input w-full text-gray-800"
                    placeholder="Enter your name"
                    {...register('name')}
                    required
                  />
                </div>
              </div>

              <div className="flex flex-wrap -mx-3 mb-4">
                <div className="w-full px-3">
                  <label className="block text-gray-800 text-sm font-medium mb-1" htmlFor="name">
                    App Name
                    <span className="text-red-600">*</span>
                  </label>
                  <div className="flex items-center justify-start gap-1">
                    <input
                      id="name"
                      type="text"
                      className=" flex-grow form-input w-full text-gray-800"
                      placeholder="Only Alphabets."
                      {...register('metadata.app_name', { pattern: /^[a-zA-Z]{1,30}$/ })}
                      required
                    />
                    <p className=" flex-none text-base font-medium">.tradly.co</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap -mx-3 mb-4">
                <div className="w-full px-3">
                  <label className="block text-gray-800 text-sm font-medium mb-1" htmlFor="email">
                    Email <span className="text-red-600">*</span>
                  </label>
                  <input
                    id="email"
                    type="email"
                    className="form-input w-full text-gray-800"
                    placeholder="Enter your email address"
                    {...register('email')}
                    required
                  />
                </div>
              </div>
              <div className="flex flex-wrap -mx-3 mb-4">
                <div className="w-full px-3">
                  <p className="block text-gray-800 text-sm font-medium mb-1" htmlFor="password">
                    Password <span className="text-red-600">*</span>
                  </p>
                  <input
                    id="password"
                    type="password"
                    className="form-input w-full text-gray-800"
                    placeholder="Enter your password"
                    {...register('password')}
                    required
                  />
                </div>
              </div>
              <div className="flex flex-wrap -mx-3 mb-4">
                <div className="w-full px-3">
                  <label
                    className="  text-gray-800 text-sm font-medium mb-1 flex items-center justify-between"
                    htmlFor="expectations"
                  >
                    <span>
                      What do you want to build & launch? <span className="text-red-600">*</span>
                    </span>
                    <span className=" flex items-center gap-1">
                      {ai_icon}
                      Ai Powered
                    </span>
                  </label>
                  <textarea
                    id="expectations"
                    type="text"
                    className="form-input w-full text-gray-800"
                    placeholder=""
                    rows={3}
                    {...register('metadata.expectations')}
                    required
                  />
                </div>
              </div>

              <div className="w-full flex items-center justify-center">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  onChange={onChange}
                  sitekey={process.env.RECAPTCHAV2_SITEKEY}
                />
              </div>
              <div className="flex flex-wrap -mx-3 mt-6">
                <div className="w-full px-3">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`btn_primary w-full flex items-center justify-center gap-4 ${
                      isLoading && 'cursor-not-allowed'
                    }`}
                  >
                    <span>Sign up</span>
                    {isLoading && <Button_loading text_color={'text-white'} />}
                  </button>
                </div>
              </div>
              <div className="text-sm text-gray-500 text-center mt-3">
                By creating an account, you agree to the{' '}
                <a className="underline" href="/terms-conditions" target={'_blank'}>
                  terms & conditions
                </a>
                , and our{' '}
                <a className="underline" href="/privacy-policy" target={'_blank'}>
                  privacy policy
                </a>
                .
              </div>
            </form>
            {/* <div className="flex items-center my-6">
								<div
									className="border-t border-gray-300 flex-grow mr-3"
									aria-hidden="true"
								></div>
								<div className="text-gray-600 italic">
									Or
								</div>
								<div
									className="border-t border-gray-300 flex-grow ml-3"
									aria-hidden="true"
								></div>
							</div>
							  */}
          </div>
        </div>
      </div>
    </>
  )
}

export default Sign_up
